import Button from "@/components/common/Button";
import { useTheme } from "@/context/useTheme";
import { useUser } from "@/context/useUser";
import { ensureReady } from "@/i18n/i18nLoader";
import { getUserInfo, setLocalLanguage } from "@/lib/storeManager";
import { ChatDMChannelList } from "../../components/ChatDMChannelList";
import adminLogo from "../../assets/adminphoto.jpg";
import { MenuService } from "@/services/APIs";
import CityCollaborationService from "@/services/APIs/CityCollaborationService";
import { useQuery } from "@tanstack/react-query";
import {
  GlobalOutlined,
  LogoutOutlined,
  MoonOutlined,
  SunOutlined,
} from "@ant-design/icons";
import { ErrorBoundary } from "@ant-design/pro-components";
import { Dropdown, Input, Menu, Switch } from "antd";
import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Bell,
  Home,
  Users,
  Calendar,
  DollarSign,
  Award,
  Heart,
  Network,
  FileText,
  MessageSquare,
  Building,
  Search,
  Tv,
  Building2,
  Handshake,
  PlusCircle,
  AlertCircle,
  Menu as MenuIcon,
  ChevronRight,
} from "lucide-react";
import { Outlet, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarProvider,
  SidebarInset,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
} from "@/projects/Civi_Circle_Admin/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/projects/Civi_Circle_Admin/components/ui/collapsible";
import { NavLink } from "@/projects/Civi_Circle_Admin/components/NavLink";

const CustomAvatar = () => {
  const { isAuthenticated, logout } = useUser();
  const { current, toggleTheme } = useTheme();
  const { i18n } = useTranslation();
  const [userInfo, setUserInfo] = useState<any>(null);
  const [langLoading, setLangLoading] = useState(false);
  const nsList = useMemo(() => ["common"], []);

  useEffect(() => {
    const info = getUserInfo();
    setUserInfo(info);
  }, [isAuthenticated]);

  const handleLogout = async () => {
    await logout();
  };

  const changeLanguage = async (lng: string) => {
    if (langLoading || lng === i18n.language) {
      return;
    }
    setLangLoading(true);
    try {
      await ensureReady({ lng, nsList });
      await i18n.changeLanguage(lng);
      setLocalLanguage(lng);
    } catch (e) {
      console.error("Failed to change language:", e);
    } finally {
      setLangLoading(false);
    }
  };

  const languageMenuItems = [
    {
      key: "en",
      label: "English",
      onClick: () => changeLanguage("en"),
      disabled: langLoading || i18n.language === "en",
    },
    {
      key: "hi",
      label: "à¤¹à¤¿à¤¨à¥à¤¦à¥€",
      onClick: () => changeLanguage("hi"),
      disabled: langLoading || i18n.language === "hi",
    },
  ];

  const userMenuOverlay = (
    <div className="mm-user-menu min-w-[220px] rounded-lg border border-border bg-card py-2 text-card-foreground shadow-lg">
      <div className="flex items-center justify-between gap-4 px-4 py-2.5">
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-muted text-muted-foreground">
            {current === "dark" ? (
              <MoonOutlined className="text-sm" />
            ) : (
              <SunOutlined className="text-sm" />
            )}
          </span>
          <span className="text-sm font-medium text-foreground">Theme</span>
        </div>
        <Switch
          checked={current === "dark"}
          onChange={toggleTheme}
          size="small"
          className="theme-switch"
          checkedChildren={<MoonOutlined className="text-xs" />}
          unCheckedChildren={<SunOutlined className="text-xs" />}
        />
      </div>
      <Dropdown
        trigger={["click"]}
        overlayClassName="mm-app"
        dropdownRender={() => (
          <div className="mm-user-menu rounded-lg border border-border bg-card py-1 text-card-foreground shadow-lg">
            <Menu
              selectable={false}
              items={languageMenuItems}
              className="!border-0 !bg-transparent"
              style={{ minWidth: 140 }}
            />
          </div>
        )}
        placement="bottomRight"
      >
        <div className="flex cursor-pointer items-center justify-between gap-4 px-4 py-2.5 hover:bg-muted/50">
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-muted text-muted-foreground">
              <GlobalOutlined className="text-sm" />
            </span>
            <span className="text-sm font-medium text-foreground">
              Language
            </span>
          </div>
          <span className="text-xs text-muted-foreground">
            {i18n.language === "hi" ? "à¤¹à¤¿à¤¨à¥à¤¦à¥€" : "EN"}
          </span>
        </div>
      </Dropdown>
      <div className="my-1 border-t border-border" />
      <button
        type="button"
        onClick={handleLogout}
        className="flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-left text-sm font-medium text-destructive hover:bg-destructive/10"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-destructive/10 text-destructive">
          <LogoutOutlined className="text-sm" />
        </span>
        Logout
      </button>
    </div>
  );

  return (
    <div className="flex items-center gap-2">
      <Dropdown
        trigger={["click"]}
        dropdownRender={() => userMenuOverlay}
        placement="bottomRight"
        overlayClassName="mm-app mm-user-dropdown-overlay"
      >
        <div className="flex cursor-pointer items-center gap-2">
          <img
            src={adminLogo}
            alt="avatar"
            className="h-12 w-12 rounded-full"
          />
          <span
            className="text-md font-medium text-foreground"
            style={{ fontFamily: "Poppins" }}
          >
            {userInfo?.full_name ??
              (userInfo?.first_name || userInfo?.last_name
                ? `${userInfo?.first_name ?? ""} ${userInfo?.last_name ?? ""}`.trim()
                : "User")}
          </span>
        </div>
      </Dropdown>
    </div>
  );
};

type MenuItem = {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
};

const MMLayout = () => {
  const { channelList, dmUserList } = ChatDMChannelList();
  const location = useLocation();
  const userInfo = useMemo(() => getUserInfo(), []);

  const { data: userRoleData } = useQuery({
    queryKey: ["userRole", userInfo?.id],
    queryFn: () => CityCollaborationService.getUserRole(userInfo?.id),
    enabled: !!userInfo?.id,
  });

  const userRole =
    userRoleData?.role_name ??
    userRoleData?.[0]?.role_name ??
    userInfo?.role_name;

  // Main menu items
  const mainNavItems: MenuItem[] = [
    { title: "Dashboard", url: "/mm/dashboard", icon: Home },
    { title: "Feed Creation", url: "/mm/feed-creation", icon: PlusCircle },
    { title: "SOP Library", url: "/mm/sops", icon: FileText },
    { title: "Departments", url: "/mm/department-list", icon: Building },
    { title: "Organization", url: "/mm/chapter-profile-list", icon: Building2 },
    { title: "Members", url: "/mm/member-list", icon: Users },
    { title: "Events", url: "/mm/events-list", icon: Calendar },
    { title: "Donation", url: "/mm/donation-list", icon: DollarSign },
    { title: "Sponsorship", url: "/mm/sponsorship-list", icon: Award },
    { title: "Relationships", url: "/mm/create-relationship", icon: Network },
    { title: "Volunteering", url: "/mm/volunteering-list", icon: Heart },
    {
      title: "City Collaboration",
      url: "/mm/city-collaborations",
      icon: Handshake,
    },
    { title: "Error Logs", url: "/mm/error-logs", icon: AlertCircle },
  ];

  // Direct Messages - flatten children into main menu
  const dmItems: MenuItem[] = dmUserList?.length
    ? dmUserList.map(d => ({
        title: (d as any)?.full_name ?? "Unknown",
        url: `/mm/chat?dmId=${d.id}&isDM=true`,
        icon: MessageSquare,
      }))
    : [];

  // Channels - flatten children into main menu
  const channelItems: MenuItem[] = channelList?.length
    ? channelList.map(c => ({
        title: c?.name ?? "Unknown Channel",
        url: `/mm/chat?channelId=${c.stream_id}&isDM=false`,
        icon: Tv,
      }))
    : [];

  const { data: menuData } = useQuery({
    queryKey: ["menus"],
    queryFn: () => MenuService.listMenus(),
  });

  const dynamicMenuItems = useMemo(() => {
    if (!menuData || !Array.isArray(menuData)) {
      return [];
    }
    return menuData.map(item => ({
      id: item.id,
      title: item.title,
      url: `/mm${item.path === "/" ? "" : item.path}`,
      icon: item.icon_url
        ? () => (
            <img
              src={item.icon_url}
              alt="icon"
              className="h-4 w-4"
              style={{ filter: "grayscale(1) brightness(0.5)" }}
            />
          )
        : FileText,
      children: item.children?.map((child: any) => ({
        id: child.id,
        title: child.title,
        url: `/mm${child.path === "/" ? "" : child.path}`,
        icon: child.icon_url
          ? () => <img src={child.icon_url} alt="icon" className="h-3 w-3" />
          : FileText,
      })),
    }));
  }, [menuData]);

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full overflow-hidden">
        <Sidebar className="border-r border-sidebar-border">
          <SidebarHeader className="border-b border-sidebar-border px-6 py-4">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                <span className="text-sm font-bold text-primary-foreground">
                  CC
                </span>
              </div>
              <span className="font-semibold text-sidebar-foreground">
                Civi Circle
              </span>
            </div>
          </SidebarHeader>

          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupLabel>Main Menu</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {mainNavItems.map(item => (
                    <SidebarMenuItem key={item.url}>
                      <SidebarMenuButton asChild>
                        <NavLink
                          to={item.url}
                          end={item.url === "/mm/dashboard"}
                          className="hover:bg-sidebar-accent/80 dark:hover:bg-sidebar-accent"
                          activeClassName="bg-sidebar-accent text-sidebar-primary font-medium dark:bg-sidebar-accent dark:text-sidebar-primary"
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            {userRole === "Super Admin" && dynamicMenuItems.length > 0 && (
              <SidebarGroup>
                <SidebarGroupLabel>Application Menu</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {dynamicMenuItems.map(item => {
                      const isChildActive = item.children?.some(
                        child => location.pathname === child.url
                      );

                      if (item.children && item.children.length > 0) {
                        return (
                          <Collapsible
                            key={item.id}
                            asChild
                            defaultOpen={isChildActive}
                            className="group/collapsible"
                          >
                            <SidebarMenuItem>
                              <CollapsibleTrigger asChild>
                                <SidebarMenuButton tooltip={item.title}>
                                  <item.icon className="h-4 w-4" />
                                  <span>{item.title}</span>
                                  <ChevronRight className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                                </SidebarMenuButton>
                              </CollapsibleTrigger>
                              <CollapsibleContent>
                                <SidebarMenuSub>
                                  {item.children.map(child => {
                                    const isActive =
                                      location.pathname === child.url;
                                    return (
                                      <SidebarMenuSubItem key={child.id}>
                                        <SidebarMenuSubButton
                                          asChild
                                          isActive={isActive}
                                        >
                                          <NavLink
                                            to={child.url}
                                            className="block rounded-md px-3 py-1 text-sm text-foreground transition-colors hover:bg-sidebar-accent"
                                          >
                                            {child.title}
                                          </NavLink>
                                        </SidebarMenuSubButton>
                                      </SidebarMenuSubItem>
                                    );
                                  })}
                                </SidebarMenuSub>
                              </CollapsibleContent>
                            </SidebarMenuItem>
                          </Collapsible>
                        );
                      }

                      return (
                        <SidebarMenuItem key={item.id}>
                          <SidebarMenuButton asChild tooltip={item.title}>
                            <NavLink
                              to={item.url}
                              className="hover:bg-sidebar-accent/80 dark:hover:bg-sidebar-accent"
                              activeClassName="bg-sidebar-accent text-sidebar-primary font-medium dark:bg-sidebar-accent dark:text-sidebar-primary"
                            >
                              <item.icon className="h-4 w-4" />
                              <span>{item.title}</span>
                            </NavLink>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}

            {dmItems.length > 0 && (
              <SidebarGroup>
                <SidebarGroupLabel className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Direct Messages
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <ul className="ml-1 list-disc space-y-1 pl-6">
                    {dmItems.map(dmItem => {
                      const urlParams = new URLSearchParams(
                        dmItem.url.split("?")[1]
                      );
                      const currentParams = new URLSearchParams(
                        location.search
                      );
                      const isActive =
                        location.pathname === "/mm/chat" &&
                        urlParams.get("dmId") === currentParams.get("dmId") &&
                        currentParams.get("isDM") === "true";

                      return (
                        <li key={dmItem.url}>
                          <NavLink
                            to={dmItem.url}
                            className={`block rounded-md px-3 py-2 text-sm transition-colors hover:bg-sidebar-accent ${
                              isActive
                                ? "bg-sidebar-accent font-medium text-primary dark:text-primary"
                                : "text-sidebar-foreground"
                            }`}
                          >
                            {dmItem.title}
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                </SidebarGroupContent>
              </SidebarGroup>
            )}

            {channelItems.length > 0 && (
              <SidebarGroup>
                <SidebarGroupLabel className="flex items-center gap-2">
                  <Tv className="h-4 w-4" />
                  Channels
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <ul className="ml-1 list-disc space-y-1 pl-6">
                    {channelItems.map(channelItem => {
                      const urlParams = new URLSearchParams(
                        channelItem.url.split("?")[1]
                      );
                      const currentParams = new URLSearchParams(
                        location.search
                      );
                      const isActive =
                        location.pathname === "/mm/chat" &&
                        urlParams.get("channelId") ===
                          currentParams.get("channelId") &&
                        currentParams.get("isDM") === "false";

                      return (
                        <li key={channelItem.url}>
                          <NavLink
                            to={channelItem.url}
                            className={`block rounded-md px-3 py-2 text-sm transition-colors hover:bg-sidebar-accent ${
                              isActive
                                ? "bg-sidebar-accent font-medium text-primary dark:text-primary"
                                : "text-sidebar-foreground"
                            }`}
                          >
                            {channelItem.title}
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                </SidebarGroupContent>
              </SidebarGroup>
            )}
          </SidebarContent>
        </Sidebar>

        <SidebarInset className="flex flex-col overflow-hidden">
          <MMHeader />
          <main className="flex-1 overflow-auto bg-background p-3 text-foreground sm:p-4 md:p-6">
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};

// Separate Header Component
const MMHeader = () => {
  const { toggleSidebar, setOpenMobile, isMobile } = useSidebar();
  const location = useLocation();

  // Auto-close sidebar on route change (mobile only)
  useEffect(() => {
    if (isMobile) {
      setOpenMobile(false);
    }
  }, [location.pathname, isMobile, setOpenMobile]);

  return (
    <header className="sticky top-0 z-50 flex-shrink-0 border-b border-border bg-card text-card-foreground">
      <div className="flex h-14 items-center justify-between gap-2 px-3 sm:h-16 sm:gap-5 sm:px-6">
        <div className="flex flex-1 items-center gap-2 sm:gap-3">
          {/* Sidebar Toggle Button - Mobile/Tablet Only */}
          <button
            onClick={toggleSidebar}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-background text-foreground transition-colors hover:bg-accent hover:text-accent-foreground active:bg-accent/80 sm:h-10 sm:w-10 md:hidden"
            aria-label="Toggle sidebar"
            title="Toggle sidebar menu"
          >
            <MenuIcon className="h-5 w-5 sm:h-5 sm:w-5" />
          </button>

          {/* Search Input - Responsive */}
          <Input
            placeholder="Search..."
            prefix={<Search size={16} className="text-muted-foreground" />}
            className="h-9 w-full max-w-xs rounded-lg border-border bg-background text-sm text-foreground placeholder:text-muted-foreground sm:h-10 sm:max-w-md"
            styles={{
              input: {
                backgroundColor: "transparent",
              },
            }}
          />
        </div>

        {/* Right Side - Notifications & Avatar */}
        <div className="flex items-center gap-2 sm:gap-5">
          <Button
            tooltip="Notifications"
            icon={<Bell size={16} className="text-foreground" />}
            className="!h-9 !w-9 !border-border !text-foreground hover:!text-foreground sm:!h-10 sm:!w-10"
          />
          <CustomAvatar />
        </div>
      </div>
    </header>
  );
};

export default MMLayout;
